import React, { useEffect, useRef } from 'react'
import classnames from 'classnames'
import formatMessage from 'format-message'
import { applyTranslatePoint } from './utils'
import Shape from './Shape'
import Annotation from './Annotation'
import { saveCommentFn } from './utils.js'
import SecondaryControls from '../SecondaryControls'
import SelectionBox from './SelectionBox'
import './InkAnnotation.css'
import { DEFAULT_INK_ANNOTATION_COLOR } from '../constants'

export default function InkAnnotation(props) {
  const {
    annotation,
    canEdit,
    controlMode,
    commentReplies,
    isHovered,
    isSelected,
    multiStrokeAnnotationId,
    onCreateAnnotation,
    onCurrentColorChange,
    onCurrentInkStrokeWidthChange,
    onUpdateAnnotation,
    selectionBoxPadding,
    sessionUserObject,
    viewportHeight,
    viewportScale,
    onMultiStrokeAnnotation,
    onDeselectAnnotation,
  } = props

  const handleCommentSave = saveCommentFn({
    annotation,
    commentReplies,
    onUpdateAnnotation,
    viewportHeight,
    viewportScale,
    onCreateAnnotation,
    sessionUserObject,
  })

  const updateColor = (color) => {
    onUpdateAnnotation(
      viewportHeight,
      viewportScale,
      { ...annotation, color },
      multiStrokeAnnotationId === annotation.id,
    )
    onCurrentColorChange(color, 'ink')
  }

  const scaleInklist = (widthMultiplier) => {
    if (widthMultiplier === 1) {
      return annotation.inklist
    }
    const gestures = annotation.inklist.gestures.map((gesture) => {
      return gesture.map((point) => {
        return point.width ? { ...point, width: point.width * widthMultiplier } : point
      })
    })
    return { ...annotation.inklist, gestures }
  }

  const updateInkStrokeWidth = (width) => {
    const widthMultiplier = width / annotation.width
    const inklist = scaleInklist(widthMultiplier)
    onUpdateAnnotation(
      viewportHeight,
      viewportScale,
      {
        ...annotation,
        width,
        inklist,
      },
      multiStrokeAnnotationId === annotation.id,
    )
    onCurrentInkStrokeWidthChange(width, 'ink')
  }

  const [[x1, y1], [x2, y2]] = annotation.rect
  const width = x2 - x1
  const height = y1 - y2

  const isSaved = typeof annotation.created_at === 'string'

  const secondaryControls = (
    <SecondaryControls
      controlMode="ink"
      currentColor={annotation.color}
      currentInkWidth={annotation.width}
      onCurrentColorChange={updateColor}
      onCurrentInkStrokeWidthChange={updateInkStrokeWidth}
    />
  )

  if (typeof annotation.inklist !== 'object') {
    throw new Error('inklist missing on InkAnnotation')
  }

  if (!(annotation.inklist.gestures instanceof Array)) {
    throw new Error('inklist.gestures missing on InkAnnotation')
  }

  const translatedGestures = annotation.inklist.gestures.map((gesture) => {
    return gesture.map(applyTranslatePoint(-x1 + selectionBoxPadding, -y2 + selectionBoxPadding))
  })

  const shapes = translatedGestures.map((gesture, index) => {
    return (
      <Shape
        key={index}
        points={gesture}
        strokeColor={annotation.color || 'black'}
        strokeWidth={annotation.width || 2}
      />
    )
  })

  const isVisible = isHovered || isSelected
  const classes = classnames('InkAnnotation', {
    'InkAnnotation--editable': canEdit,
    'InkAnnotation--saved': isSaved || annotation?.savedFromSession,
  })

  const lastControlMode = useRef(controlMode)
  useEffect(() => {
    if (
      lastControlMode.current === 'ink' &&
      controlMode !== 'ink' &&
      multiStrokeAnnotationId === annotation.id
    ) {
      onMultiStrokeAnnotation(null)
      onCreateAnnotation(viewportHeight, viewportScale, annotation)
      onDeselectAnnotation(annotation.id)
    }
    lastControlMode.current = controlMode
  }, [controlMode])

  return (
    <Annotation
      {...props}
      className={classes}
      iconColor={annotation.color ? annotation.color : DEFAULT_INK_ANNOTATION_COLOR}
      contents={annotation.contents}
      height={height}
      id={annotation.id}
      isMovable={true}
      onCommentSave={handleCommentSave}
      rect={annotation.rect}
      render={(_isSvgContainerFocused) => [
        <SelectionBox
          key="selectionBox"
          height={height + selectionBoxPadding * 2}
          isSelected={isSelected}
          isVisible={isVisible}
          width={width + selectionBoxPadding * 2}
        />,
        shapes,
      ]}
      secondaryControls={secondaryControls}
      selectionBoxPadding={selectionBoxPadding}
      title={formatMessage('Free draw annotation')}
      user_name={annotation.user_name}
      width={width}
      x={x1}
      y={y2}
    />
  )
}
